/**
 * §P.9 fase 2 · Horas de entrenador, base para nóminas.
 *
 * ## Un fichaje sin salida no cuenta, y eso se nota
 *
 * «Es mejor una nómina corta que una inventada, y **lo que falta se ve en los
 * días**.» Esa última frase es la instrucción para esta pantalla: las horas solas no
 * dicen si hay fichajes sin cerrar, pero **horas entre días** sí. Doce días y seis
 * horas no es media hora al día: son seis salidas sin fichar.
 *
 * Así que la pantalla calcula esa división y avisa. No corrige nada —los fichajes se
 * arreglan en su sitio— pero evita que alguien pague una nómina corta creyendo que el
 * entrenador trabajó poco.
 *
 * ## Es base para nóminas, no una nómina
 *
 * Y hay que decirlo donde se lee: nadie debería pasar esta cifra a una gestoría sin
 * saber que sale del fichaje y que el fichaje se olvida.
 */
import { api } from '@/shared/api/client';
import { ErrorDeApi, aProblema } from '@/shared/api/errores';
import { numero } from '@/shared/ui/formato';
import { EnvolturaDeInforme } from '@/club/informes/Envoltura';
import {
  horasPorDia,
  SOSPECHA_HORAS_POR_DIA,
  type FilaDeHorasDeEntrenador,
  type Informe,
} from '@/club/informes/tipos';

export function HorasDeEntrenador({ club }: { club: string }) {
  return (
    <EnvolturaDeInforme<FilaDeHorasDeEntrenador>
      club={club}
      clave="horas"
      ruta="coach-hours"
      titulo="Horas de entrenador"
      explicacion={
        <>
          Horas de cada entrenador según su fichaje.{' '}
          <strong>Es base para la nómina, no la nómina</strong>: cuenta entradas
          emparejadas con salidas, y un fichaje sin cerrar no cuenta.
        </>
      }
      vacio={{
        titulo: 'Nadie ha fichado todavía',
        explicacion:
          'Este informe cuenta entradas y salidas del fichaje. Sin fichajes no hay horas que sumar: es lo normal si el club todavía no lo usa.',
      }}
      pedir={async () => {
        const { data, error } = await api.v1
          .clubs({ club })
          .reports['coach-hours'].get({ query: {} });
        if (error) throw new ErrorDeApi(aProblema(error));
        return data as unknown as Informe<FilaDeHorasDeEntrenador>;
      }}
    >
      {(datos) => {
        const sospechosos = datos.rows.filter((f) => {
          const p = horasPorDia(f);
          return p !== null && p < SOSPECHA_HORAS_POR_DIA;
        });

        return (
          <>
            {/*
              El aviso que da valor a la pantalla: la cifra corta casi nunca es que
              trabajara poco.
            */}
            {sospechosos.length > 0 && (
              <div className="aviso" role="status">
                <strong>
                  {sospechosos.length === 1
                    ? 'A alguien le salen menos de una hora por día trabajado.'
                    : `A ${sospechosos.length} personas les salen menos de una hora por día trabajado.`}
                </strong>{' '}
                Casi siempre son <strong>salidas sin fichar</strong> y no
                jornadas cortas: un fichaje sin cerrar no suma horas, pero el
                día sí cuenta. Conviene revisarlo antes de pasar esto a la
                gestoría.
              </div>
            )}

            <dl className="datos">
              <div>
                <dt>Horas en total</dt>
                <dd>{numero(datos.totals['hours'] ?? 0)}</dd>
              </div>
              <div>
                <dt>Clases dadas</dt>
                <dd>{datos.totals['sessions_taught'] ?? 0}</dd>
              </div>
            </dl>

            <div className="tabla-envoltura">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Quién</th>
                    <th className="num">Días</th>
                    <th className="num">Horas</th>
                    <th className="num">Horas por día</th>
                    <th className="num">Clases</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.rows.map((f) => {
                    const porDia = horasPorDia(f);
                    const raro =
                      porDia !== null && porDia < SOSPECHA_HORAS_POR_DIA;

                    return (
                      <tr key={f.user_id}>
                        <td>{f.name}</td>
                        <td className="num">{f.days}</td>
                        <td className="num">{numero(f.hours)}</td>
                        <td className="num">
                          {porDia === null ? (
                            '—'
                          ) : raro ? (
                            <span className="pastilla pastilla-paused">
                              {numero(porDia)}
                            </span>
                          ) : (
                            numero(porDia)
                          )}
                        </td>
                        <td className="num">{f.sessions_taught}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        );
      }}
    </EnvolturaDeInforme>
  );
}
